import { useState, useCallback, useEffect } from 'react';
import type { ExpandedAccordionState, ExpandedAccordionSection } from '../types/careExpanded';
import { DEFAULT_EXPANDED_ACCORDION_STATE } from '../types/careExpanded';

interface UseExpandedAccordionStateReturn {
  state: ExpandedAccordionState;
  toggleSection: (section: ExpandedAccordionSection) => void;
  expandSection: (section: ExpandedAccordionSection) => void;
  collapseSection: (section: ExpandedAccordionSection) => void;
  collapseAll: () => void;
  expandAll: () => void;
  isExpanded: (section: ExpandedAccordionSection) => boolean;
  expandedSet: Set<ExpandedAccordionSection>;
}

/**
 * Hook for managing expanded accordion state with LocalStorage persistence.
 *
 * State is persisted per plant ID to remember user preferences.
 * Default: Light section expanded, all others collapsed.
 */
export function useExpandedAccordionState(plantId: string): UseExpandedAccordionStateReturn {
  const storageKey = `expanded-accordion-state-${plantId}`;

  const [state, setState] = useState<ExpandedAccordionState>(() => {
    if (typeof window === 'undefined') return DEFAULT_EXPANDED_ACCORDION_STATE;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isValidExpandedAccordionState(parsed)) {
          return parsed;
        }
      }
    } catch {
      // Ignore parse errors, use defaults
    }
    return DEFAULT_EXPANDED_ACCORDION_STATE;
  });

  // Persist state changes to LocalStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Ignore storage errors (e.g., quota exceeded)
    }
  }, [state, storageKey]);

  const toggleSection = useCallback((section: ExpandedAccordionSection) => {
    setState((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const expandSection = useCallback((section: ExpandedAccordionSection) => {
    setState((prev) => ({
      ...prev,
      [section]: true,
    }));
  }, []);

  const collapseSection = useCallback((section: ExpandedAccordionSection) => {
    setState((prev) => ({
      ...prev,
      [section]: false,
    }));
  }, []);

  const collapseAll = useCallback(() => {
    setState({
      light: false,
      watering: false,
      feeding: false,
      potting: false,
      soilRecipes: false,
      pruning: false,
      propagation: false,
      troubleshooting: false,
      seasonal: false,
      notes: false,
    });
  }, []);

  const expandAll = useCallback(() => {
    setState({
      light: true,
      watering: true,
      feeding: true,
      potting: true,
      soilRecipes: true,
      pruning: true,
      propagation: true,
      troubleshooting: true,
      seasonal: true,
      notes: true,
    });
  }, []);

  const isExpanded = useCallback(
    (section: ExpandedAccordionSection) => {
      return state[section];
    },
    [state]
  );

  // Convert state to Set for CareAccordion compatibility
  const expandedSet = new Set(
    (Object.entries(state) as [ExpandedAccordionSection, boolean][])
      .filter(([, isOpen]) => isOpen)
      .map(([section]) => section)
  );

  return {
    state,
    toggleSection,
    expandSection,
    collapseSection,
    collapseAll,
    expandAll,
    isExpanded,
    expandedSet,
  };
}

/**
 * Validate that an object has all required ExpandedAccordionState keys.
 */
function isValidExpandedAccordionState(obj: unknown): obj is ExpandedAccordionState {
  if (typeof obj !== 'object' || obj === null) return false;

  const requiredKeys: ExpandedAccordionSection[] = [
    'light',
    'watering',
    'feeding',
    'potting',
    'soilRecipes',
    'pruning',
    'propagation',
    'troubleshooting',
    'seasonal',
    'notes',
  ];

  for (const key of requiredKeys) {
    if (typeof (obj as Record<string, unknown>)[key] !== 'boolean') {
      return false;
    }
  }

  return true;
}
